import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, XCircle, Eye, ArrowRight, GripVertical, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QuestionRenderer, ExplanationRenderer } from "@/components/QuestionRenderer";
import { LatexText } from "@/components/LatexText";
import { MathRenderer } from "@/components/MathRenderer";
import DOMPurify from "dompurify";

export interface PreviewQuestion {
  questionText: string;
  questionImageUrl?: string | null;
  questionMath?: string | null;
  options?: string | Array<{ id?: string; text: string; imageUrl?: string; math?: string }> | null;
  correctAnswer: string;
  explanation?: string | null;
  explanationImageUrl?: string | null;
  explanationMath?: string | null;
  difficulty?: string;
  subject?: string;
  grade?: number;
  topic?: string;
  questionType?: string;
}

interface QuestionPreviewDialogProps {
  question: PreviewQuestion | null;
  open: boolean;
  onClose: () => void;
  onEdit?: () => void;
}

function parseOptions(raw: PreviewQuestion["options"]): Array<{ id: string; text: string; imageUrl?: string; math?: string }> {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (typeof parsed === "string") return JSON.parse(parsed);
    if (Array.isArray(parsed)) return parsed;
  } catch {}
  return [];
}

function getDifficultyStyle(difficulty?: string) {
  if (difficulty === "easy") return "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400";
  if (difficulty === "hard") return "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400";
  return "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400";
}

function getSubjectLabel(subject?: string) {
  if (!subject) return "";
  return subject.charAt(0).toUpperCase() + subject.slice(1);
}

/** Try to parse matching/ordering/grid data from options */
function parseStructuredOptions(raw: PreviewQuestion["options"]): any {
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    // If it's an array with a structured object as first element
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === "object") {
      if ("prompts" in parsed[0] || "question_rows" in parsed[0]) return parsed[0];
    }
    // If it's directly a structured object
    if (typeof parsed === "object" && !Array.isArray(parsed)) {
      if ("prompts" in parsed || "question_rows" in parsed) return parsed;
    }
  } catch {}
  return null;
}

/** Parse correct answer which may be JSON */
function parseCorrectAnswer(raw: string): any {
  try { return JSON.parse(raw); } catch { return raw; }
}

export function QuestionPreviewDialog({ question, open, onClose, onEdit }: QuestionPreviewDialogProps) {
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [matchingSelections, setMatchingSelections] = useState<Record<string, string>>({});
  const [activePrompt, setActivePrompt] = useState<string | null>(null);
  const [orderingOrder, setOrderingOrder] = useState<string[]>([]);
  const [showAnswers, setShowAnswers] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [touchDragIdx, setTouchDragIdx] = useState<number | null>(null);
  const [ghostPos, setGhostPos] = useState<{ x: number; y: number; text: string } | null>(null);
  const orderListRef = useRef<HTMLDivElement>(null);
  const touchRef = useRef<{ startY: number; idx: number; moved: boolean; holdTimer?: ReturnType<typeof setTimeout> } | null>(null);
  const scrollLockRef = useRef(false);
  const answered = selectedAnswer !== null;

  useEffect(() => {
    const prevent = (e: TouchEvent) => {
      if (scrollLockRef.current) e.preventDefault();
    };
    document.addEventListener('touchmove', prevent, { passive: false });
    return () => document.removeEventListener('touchmove', prevent);
  }, []);

  if (!question) return null;

  const options = parseOptions(question.options);
  const structuredOpts = parseStructuredOptions(question.options);
  const qType = question.questionType || "multiple-choice";

  const getCorrectOptionId = (): string => {
    if (!options.length) return question.correctAnswer;
    if (options.some(o => o.id === question.correctAnswer)) return question.correctAnswer;
    const idx = options.findIndex(o => o.text === question.correctAnswer);
    if (idx >= 0) return options[idx].id || `legacy_${idx}`;
    return question.correctAnswer;
  };

  const correctOptionId = getCorrectOptionId();

  const handleOptionClick = (optionId: string) => {
    if (!answered) setSelectedAnswer(optionId);
  };

  const handleReset = () => {
    setSelectedAnswer(null);
    setMatchingSelections({});
    setActivePrompt(null);
    setOrderingOrder([]);
    setShowAnswers(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); handleReset(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 pr-6">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                Student Preview
              </DialogTitle>
              <DialogDescription>
                This is exactly how students see this question on the platform.
              </DialogDescription>
            </div>
            {onEdit && (
              <Button size="sm" variant="outline" className="shrink-0 gap-1.5" onClick={() => { onClose(); handleReset(); onEdit(); }}>
                <Pencil className="w-3.5 h-3.5" /> Edit
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Metadata bar */}
        <div className="flex flex-wrap gap-2 pb-2 border-b">
          {question.grade && <Badge variant="secondary">Grade {question.grade}</Badge>}
          {question.subject && <Badge variant="secondary">{getSubjectLabel(question.subject)}</Badge>}
          {question.topic && <Badge variant="outline" className="max-w-[200px] truncate">{question.topic}</Badge>}
          {question.difficulty && (
            <Badge className={getDifficultyStyle(question.difficulty)}>
              {question.difficulty.charAt(0).toUpperCase() + question.difficulty.slice(1)}
            </Badge>
          )}
          {question.questionType && <Badge variant="outline">{question.questionType}</Badge>}
        </div>

        {/* The student-facing card */}
        <Card className="border-2">
          <CardHeader className="p-4 md:p-6">
            <QuestionRenderer
              text={question.questionText || "(no question text)"}
              imageUrl={question.questionImageUrl ?? undefined}
              mathExpression={question.questionMath ?? undefined}
              className="text-base sm:text-lg leading-snug sm:leading-relaxed"
            />
          </CardHeader>

          <CardContent className="space-y-3 p-4 md:p-6 pt-0">
            {/* Multiple Choice */}
            {(qType === "multiple-choice" || qType === "true-false") && options.length > 0 && (
              <div className="space-y-3">
                {!answered && (
                  <p className="text-xs text-muted-foreground italic">Click an option to see the correct answer</p>
                )}
                {options.map((option, index) => {
                  const optionId = option.id || `legacy_${index}`;
                  const letter = String.fromCharCode(65 + index);
                  const isSelected = selectedAnswer === optionId;
                  const isCorrect = optionId === correctOptionId;

                  let cls = "w-full p-3 sm:p-4 text-left border-2 rounded-lg transition-all min-h-12 ";
                  if (answered) {
                    if (isCorrect) cls += "bg-green-50 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-400";
                    else if (isSelected) cls += "bg-red-50 dark:bg-red-900/30 border-red-500 text-red-700 dark:text-red-400";
                    else cls += "bg-muted/30 border-muted text-muted-foreground";
                  } else {
                    cls += isSelected
                      ? "bg-primary/10 border-primary"
                      : "bg-background border-muted hover:border-primary/50 hover:bg-primary/5 cursor-pointer";
                  }

                  return (
                    <button
                      key={optionId}
                      className={cls}
                      onClick={() => handleOptionClick(optionId)}
                      disabled={answered}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border-2 flex-shrink-0 mt-1 ${
                          answered && isCorrect ? "border-green-500 bg-green-500 text-white" :
                          answered && isSelected ? "border-red-500 bg-red-500 text-white" :
                          isSelected ? "border-primary bg-primary text-white" : "border-muted"
                        }`}>
                          {answered && isCorrect ? (
                            <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          ) : answered && isSelected ? (
                            <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          ) : (
                            <span className="font-bold text-sm">{letter}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {/<[^>]+>/.test(option.text) ? (
                            <div
                              className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert"
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option.text) }}
                            />
                          ) : (
                            <div className="text-sm leading-relaxed whitespace-pre-wrap">
                              <LatexText text={option.text} />
                            </div>
                          )}
                          {option.math && (
                            <div className="py-1">
                              <MathRenderer math={option.math} displayMode={false} className="text-sm" />
                            </div>
                          )}
                          {option.imageUrl && (
                            <img
                              src={option.imageUrl}
                              alt={`Option ${letter}`}
                              className="mt-2 max-w-full max-h-32 object-contain rounded border border-border"
                            />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Multi-select */}
            {qType === "multi-select" && options.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Select all correct answers:</p>
                {options.map((option, index) => {
                  const optionId = option.id || `legacy_${index}`;
                  const letter = String.fromCharCode(65 + index);
                  let correctIds: string[];
                  try {
                    const parsed = JSON.parse(question.correctAnswer);
                    correctIds = Array.isArray(parsed) ? parsed : [String(parsed)];
                  } catch {
                    correctIds = question.correctAnswer.split(",").map(s => s.trim());
                  }
                  const isCorrect = correctIds.includes(optionId);

                  return (
                    <div
                      key={optionId}
                      className={`flex items-start gap-3 p-3 rounded-lg border-2 ${
                        isCorrect
                          ? "bg-green-50 dark:bg-green-900/30 border-green-500"
                          : "bg-muted/30 border-muted"
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 flex-shrink-0 mt-1 ${
                        isCorrect ? "border-green-500 bg-green-500 text-white" : "border-muted"
                      }`}>
                        {isCorrect ? <CheckCircle className="w-3.5 h-3.5" /> : <span className="font-bold text-sm">{letter}</span>}
                      </div>
                      <div className="flex-1 text-sm"><LatexText text={option.text} /></div>
                    </div>
                  );
                })}
                <p className="text-xs text-muted-foreground italic">Green options are the correct answers.</p>
              </div>
            )}

            {/* Matching */}
            {qType === "matching" && structuredOpts?.prompts && (
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground">Tap a term, then tap its match.</p>
                {(() => {
                  const prompts: Record<string, string> = structuredOpts.prompts || {};
                  const answers: Record<string, string> = structuredOpts.answers || {};
                  const promptKeys = Object.keys(prompts);
                  const answerKeys = Object.keys(answers);
                  const usedAnswers = new Set(Object.values(matchingSelections));
                  const correctMatch = parseCorrectAnswer(question.correctAnswer) as Record<string, string>;
                  const allMatched = Object.keys(matchingSelections).length === promptKeys.length;

                  const assign = (pKey: string, aKey: string) => {
                    setMatchingSelections({ ...matchingSelections, [pKey]: aKey });
                    setActivePrompt(null);
                  };

                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Terms</p>
                          {promptKeys.map((pKey) => {
                            const isActive = activePrompt === pKey;
                            const selectedAKey = matchingSelections[pKey];
                            const isCorrectPair = showAnswers && selectedAKey === correctMatch?.[pKey];
                            const isWrongPair = showAnswers && selectedAKey && selectedAKey !== correctMatch?.[pKey];
                            const matchColor = selectedAKey ? `${['#6366f1','#8b5cf6','#06b6d4','#ec4899','#3b82f6','#a855f7','#14b8a6','#f472b6'][promptKeys.indexOf(pKey) % 8]}` : undefined;

                            return (
                              <button
                                key={pKey}
                                onClick={() => {
                                  if (showAnswers) return;
                                  if (selectedAKey) {
                                    const next = { ...matchingSelections };
                                    delete next[pKey];
                                    setMatchingSelections(next);
                                  } else {
                                    setActivePrompt(isActive ? null : pKey);
                                  }
                                }}
                                className={`w-full p-3 border-2 rounded-lg text-sm text-left transition-all ${
                                  isCorrectPair ? "border-green-500 bg-green-50 dark:bg-green-900/30" :
                                  isWrongPair ? "border-red-500 bg-red-50 dark:bg-red-900/30" :
                                  isActive ? "border-primary bg-primary/10 ring-2 ring-primary/30 scale-[1.02]" :
                                  selectedAKey ? "bg-muted/50" :
                                  "border-border hover:border-primary/50"
                                }`}
                                style={selectedAKey && !showAnswers ? { borderColor: matchColor, borderLeftWidth: 4 } : undefined}
                              >
                                <span className="font-semibold"><LatexText text={prompts[pKey]} /></span>
                                {selectedAKey && !showAnswers && <span className="block text-xs text-muted-foreground mt-0.5">✓ matched</span>}
                                {showAnswers && isWrongPair && <span className="block text-xs text-muted-foreground mt-0.5">✗ <LatexText text={answers[correctMatch?.[pKey]] || "?"} /></span>}
                              </button>
                            );
                          })}
                        </div>

                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Answers</p>
                          {answerKeys.map((aKey) => {
                            const isUsed = usedAnswers.has(aKey);
                            const matchedPrompt = isUsed ? Object.entries(matchingSelections).find(([_, v]) => v === aKey)?.[0] : null;
                            const matchColor = matchedPrompt ? `${['#6366f1','#8b5cf6','#06b6d4','#ec4899','#3b82f6','#a855f7','#14b8a6','#f472b6'][promptKeys.indexOf(matchedPrompt) % 8]}` : undefined;
                            const isCorrect = showAnswers && matchedPrompt && matchingSelections[matchedPrompt] === correctMatch?.[matchedPrompt];

                            return (
                              <button
                                key={aKey}
                                onClick={() => {
                                  if (showAnswers) return;
                                  if (isUsed) {
                                    const pk = Object.entries(matchingSelections).find(([_, v]) => v === aKey)?.[0];
                                    if (pk) { const next = { ...matchingSelections }; delete next[pk]; setMatchingSelections(next); }
                                    return;
                                  }
                                  if (activePrompt) {
                                    assign(activePrompt, aKey);
                                  } else {
                                    const first = promptKeys.find(pk => !matchingSelections[pk]);
                                    if (first) { setActivePrompt(first); assign(first, aKey); }
                                  }
                                }}
                                disabled={showAnswers}
                                className={`w-full p-3 border-2 rounded-lg text-sm text-left transition-all ${
                                  showAnswers && isCorrect ? "border-green-500 bg-green-50 dark:bg-green-900/30" :
                                  showAnswers && isUsed ? "border-red-500 bg-red-50 dark:bg-red-900/30" :
                                  isUsed ? "opacity-50 bg-muted/30" :
                                  activePrompt ? "border-primary/50 hover:border-primary hover:bg-primary/5 hover:scale-[1.02]" :
                                  "border-border hover:border-primary/50"
                                }`}
                                style={isUsed && !showAnswers ? { borderColor: matchColor, borderLeftWidth: 4 } : undefined}
                              >
                                <LatexText text={answers[aKey]} />
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {allMatched && !showAnswers && (
                        <button onClick={() => setShowAnswers(true)} className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90">
                          Check Answers
                        </button>
                      )}
                      {showAnswers && correctMatch && (
                        <div className="p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 text-sm">
                          <p className="font-medium text-blue-600 dark:text-blue-400 text-xs uppercase mb-1">Correct Matching</p>
                          {promptKeys.map(pk => (
                            <div key={pk} className="flex items-center gap-1 text-xs">
                              <span><LatexText text={prompts[pk]} /></span>
                              <ArrowRight className="w-3 h-3" />
                              <span className="font-medium"><LatexText text={answers[correctMatch[pk]] || "?"} /></span>
                            </div>
                          ))}
                        </div>
                      )}
                      {!showAnswers && Object.keys(matchingSelections).length > 0 && (
                        <button className="text-xs text-muted-foreground underline" onClick={() => { setMatchingSelections({}); setActivePrompt(null); }}>
                          Reset all matches
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Ordering */}
            {(qType === "ordering" || qType === "order-match") && options.length > 0 && (
              <div className="space-y-3 relative">
                <p className="text-xs text-muted-foreground">Hold and drag to reorder.</p>
                {(() => {
                  const correctOrder = parseCorrectAnswer(question.correctAnswer);
                  const items = orderingOrder.length > 0
                    ? orderingOrder.map(id => options.find(o => o.id === id) || { id, text: id })
                    : [...options].sort(() => Math.random() - 0.5);

                  if (orderingOrder.length === 0 && options.length > 0) {
                    const shuffled = [...options].sort(() => Math.random() - 0.5);
                    setTimeout(() => setOrderingOrder(shuffled.map(o => o.id)), 0);
                  }

                  return (
                    <div className="space-y-2" ref={orderListRef}>
                      {items.map((item, idx) => {
                        const isCorrectPos = showAnswers && Array.isArray(correctOrder) && correctOrder[idx] === item.id;
                        const isBeingDragged = touchDragIdx === idx;
                        const plainText = item.text.replace(/<[^>]+>/g, '');
                        return (
                          <div
                            key={item.id}
                            draggable={!showAnswers}
                            onDragStart={() => setDragIdx(idx)}
                            onDragOver={(e) => {
                              e.preventDefault();
                              if (dragIdx === null || dragIdx === idx) return;
                              const newOrder = [...orderingOrder];
                              const dragged = newOrder[dragIdx];
                              newOrder.splice(dragIdx, 1);
                              newOrder.splice(idx, 0, dragged);
                              setOrderingOrder(newOrder);
                              setDragIdx(idx);
                            }}
                            onDragEnd={() => setDragIdx(null)}
                            onTouchStart={(e) => {
                              if (showAnswers) return;
                              const touch = e.touches[0];
                              const ref = { startY: touch.clientY, idx, moved: false, holdTimer: undefined as ReturnType<typeof setTimeout> | undefined };
                              ref.holdTimer = setTimeout(() => {
                                ref.moved = true;
                                setTouchDragIdx(idx);
                                setGhostPos({ x: touch.clientX, y: touch.clientY, text: plainText });
                                scrollLockRef.current = true;
                                if (navigator.vibrate) navigator.vibrate(30);
                              }, 200);
                              touchRef.current = ref;
                            }}
                            onTouchMove={(e) => {
                              if (!touchRef.current || showAnswers) return;
                              const touch = e.touches[0];
                              if (!touchRef.current.moved) {
                                const dy = Math.abs(touch.clientY - touchRef.current.startY);
                                if (dy > 10) {
                                  if (touchRef.current.holdTimer) clearTimeout(touchRef.current.holdTimer);
                                  touchRef.current = null;
                                }
                                return;
                              }
                              e.preventDefault();
                              setGhostPos({ x: touch.clientX, y: touch.clientY, text: items[touchRef.current.idx]?.text?.replace(/<[^>]+>/g, '') || '' });
                              const container = orderListRef.current;
                              if (!container) return;
                              const children = Array.from(container.children) as HTMLElement[];
                              const overIdx = children.findIndex(c => {
                                const r = c.getBoundingClientRect();
                                return touch.clientY >= r.top && touch.clientY <= r.bottom;
                              });
                              if (overIdx >= 0 && overIdx !== touchRef.current.idx) {
                                const newOrder = [...orderingOrder];
                                const dragged = newOrder[touchRef.current.idx];
                                newOrder.splice(touchRef.current.idx, 1);
                                newOrder.splice(overIdx, 0, dragged);
                                setOrderingOrder(newOrder);
                                touchRef.current.idx = overIdx;
                                setTouchDragIdx(overIdx);
                              }
                            }}
                            onTouchEnd={() => {
                              if (touchRef.current?.holdTimer) clearTimeout(touchRef.current.holdTimer);
                              touchRef.current = null;
                              setTouchDragIdx(null);
                              setGhostPos(null);
                              scrollLockRef.current = false;
                            }}
                            onTouchCancel={() => {
                              if (touchRef.current?.holdTimer) clearTimeout(touchRef.current.holdTimer);
                              touchRef.current = null;
                              setTouchDragIdx(null);
                              setGhostPos(null);
                              scrollLockRef.current = false;
                            }}
                            className={`flex items-center gap-3 p-3 border-2 rounded-lg text-sm select-none ${
                              showAnswers
                                ? isCorrectPos
                                  ? "border-green-500 bg-green-50 dark:bg-green-900/30"
                                  : "border-red-500 bg-red-50 dark:bg-red-900/30"
                                : isBeingDragged
                                  ? "opacity-30 border-primary border-dashed"
                                  : dragIdx === idx
                                    ? "opacity-50 scale-95 border-primary"
                                    : "border-border cursor-move hover:border-primary/50"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <GripVertical className="w-4 h-4 text-muted-foreground" />
                              <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">{idx + 1}</span>
                            </div>
                            <span className="flex-1"><LatexText text={item.text} /></span>
                          </div>
                        );
                      })}
                      {!showAnswers && orderingOrder.length > 0 && (
                        <button
                          onClick={() => setShowAnswers(true)}
                          className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90"
                        >
                          Check Order
                        </button>
                      )}
                      {showAnswers && Array.isArray(correctOrder) && (
                        <div className="p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 text-sm">
                          <p className="font-medium text-blue-600 dark:text-blue-400 text-xs uppercase mb-1">Correct Order</p>
                          {correctOrder.map((id: string, idx: number) => {
                            const item = options.find(o => o.id === id);
                            return (
                              <div key={id} className="text-xs">
                                {idx + 1}. <LatexText text={item?.text || id} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {ghostPos && (
                  <div
                    className="fixed z-50 pointer-events-none px-4 py-3 bg-primary text-primary-foreground rounded-lg shadow-2xl text-sm font-medium max-w-[80vw] truncate border-2 border-primary"
                    style={{ left: ghostPos.x - 100, top: ghostPos.y - 25, transform: 'scale(1.05) rotate(1deg)', opacity: 0.95 }}
                  >
                    {ghostPos.text}
                  </div>
                )}
              </div>
            )}

            {/* Table Grid / Categorising */}
            {(qType === "table-grid" || qType === "categorising" || qType === "er-match") && structuredOpts?.question_rows && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  {qType === "categorising" ? "Sort each item into the correct category." : "Select the correct option for each row."}
                </p>
                {(() => {
                  const rows: Record<string, string> = structuredOpts.question_rows || {};
                  const cols: Record<string, string> = structuredOpts.answer_columns || {};
                  const correctAnswers = parseCorrectAnswer(question.correctAnswer) as Record<string, string[]>;
                  const rowKeys = Object.keys(rows);
                  const colKeys = Object.keys(cols);

                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr>
                            <th className="text-left p-2 border-b font-medium">{structuredOpts.question_header || ""}</th>
                            {colKeys.map(ck => (
                              <th key={ck} className="text-center p-2 border-b font-medium">{cols[ck]}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rowKeys.map(rk => (
                            <tr key={rk} className="border-b last:border-0">
                              <td className="p-2 font-medium"><LatexText text={rows[rk]} /></td>
                              {colKeys.map(ck => {
                                const isCorrect = correctAnswers?.[rk]?.includes(ck);
                                return (
                                  <td key={ck} className="text-center p-2">
                                    <div className={`w-6 h-6 rounded-full mx-auto border-2 flex items-center justify-center ${
                                      isCorrect ? "border-green-500 bg-green-500 text-white" : "border-muted"
                                    }`}>
                                      {isCorrect && <CheckCircle className="w-3.5 h-3.5" />}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Fill blank / short answer / other types with no interactive options */}
            {!["multiple-choice", "true-false", "multi-select", "matching", "ordering", "order-match", "table-grid", "categorising", "er-match"].includes(qType) && question.correctAnswer && options.length === 0 && !structuredOpts && (
              <div className="p-3 rounded-lg border-2 border-green-500 bg-green-50 dark:bg-green-900/30">
                <p className="text-xs text-muted-foreground mb-1">Correct Answer</p>
                <p className="font-semibold text-green-700 dark:text-green-400">
                  <LatexText text={question.correctAnswer} />
                </p>
              </div>
            )}

            {/* Explanation: shown always in preview so admin can review it */}
            {question.explanation && (
              <>
                <Separator className="mt-2" />
                <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4">
                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-2">Explanation</p>
                  <ExplanationRenderer
                    text={question.explanation}
                    imageUrl={question.explanationImageUrl ?? undefined}
                    mathExpression={question.explanationMath ?? undefined}
                  />
                </div>
              </>
            )}

            {(answered || showAnswers) && (
              <button
                className="w-full text-xs text-muted-foreground underline mt-1 text-center"
                onClick={handleReset}
              >
                Reset answer
              </button>
            )}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
